import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Plus,
  ChevronDown,
  ChevronRight,
  Users,
  MapPin,
  Calendar,
  X,
  UserPlus,
  UserMinus,
  Save,
  Copy,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'

/* ── Types ── */

interface Tour {
  id: string
  name: string
  description: string | null
  status: string
  startDate: string | null
  endDate: string | null
  memberCount: number
  legCount: number
}

interface TourMember {
  id: string
  name: string
  role: string
}

interface TourLeg {
  id: string
  name: string
  startDate: string | null
  endDate: string | null
  sortOrder: number
  shows: LegShow[]
  overrides: LegOverride[]
}

interface LegShow {
  id: string
  showId: string
  title: string
  date: string
  venue: string
  city: string
}

interface LegOverride {
  id: string
  name: string
  role: string
  action: 'add' | 'remove'
  reason: string | null
}

interface RosterMember {
  name: string
  role: string
  source: string
}

interface Show {
  id: string
  title: string
  date: string
  venue: string
  city: string
}

/* ── Helpers ── */

/* ── Role picker with inline "add new" ── */

interface AllRole {
  value: string
  label: string
  group: string
  isCustom: boolean
}

function RolePickerWithAdd({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const [addingNew, setAddingNew] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [saving, setSaving] = useState(false)

  const roles = useQuery<AllRole[], ApiClientError>({
    queryKey: ['all-roles'],
    queryFn: () => artistFetch<AllRole[]>('/custom-roles/all'),
    staleTime: 60000,
  })

  const qc = useQueryClient()

  async function addCustomRole() {
    if (!newRoleName.trim()) return
    setSaving(true)
    try {
      await artistFetch('/custom-roles', {
        method: 'POST',
        body: { name: newRoleName.trim(), group: 'management_and_ops' },
      })
      qc.invalidateQueries({ queryKey: ['all-roles'] })
      onChange(newRoleName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_'))
      setNewRoleName('')
      setAddingNew(false)
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error creating role')
    } finally {
      setSaving(false)
    }
  }

  if (addingNew) {
    return (
      <div className="flex gap-1">
        <input
          value={newRoleName}
          onChange={(e) => setNewRoleName(e.target.value)}
          className="flex-1 rounded-md border border-ovation-ink/15 px-2 py-2 text-sm"
          placeholder="New role name"
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomRole() } }}
        />
        <button
          type="button"
          onClick={addCustomRole}
          disabled={saving || !newRoleName.trim()}
          className="rounded-md bg-ovation-ink px-2 py-1 text-xs text-ovation-paper disabled:opacity-50"
        >
          {saving ? '...' : 'Add'}
        </button>
        <button
          type="button"
          onClick={() => setAddingNew(false)}
          className="text-xs text-ovation-muted"
        >
          Cancel
        </button>
      </div>
    )
  }

  return (
    <div className="flex gap-1">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm bg-white"
      >
        <option value="">— select role —</option>
        {(roles.data ?? []).map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}{r.isCustom ? ' (custom)' : ''}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => setAddingNew(true)}
        className="shrink-0 rounded-md border border-ovation-ink/15 px-2 py-1 text-ovation-muted hover:text-ovation-ink hover:bg-ovation-ink/5"
        title="Add new role"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  )
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString()
}

function dateRange(start: string | null, end: string | null): string {
  if (!start && !end) return 'No dates set'
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`
  if (start) return `From ${formatDate(start)}`
  return `Until ${formatDate(end)}`
}

/* ── Main page ── */

export function ToursPage() {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [expandedTourId, setExpandedTourId] = useState<string | null>(null)

  const { data, error, isLoading } = useQuery<Tour[], ApiClientError>({
    queryKey: ['tours'],
    queryFn: () => artistFetch<Tour[]>('/tours'),
  })

  function toggleTour(tourId: string) {
    setExpandedTourId((prev) => (prev === tourId ? null : tourId))
  }

  async function deleteTour(tourId: string) {
    if (!confirm('Delete this tour?')) return
    try {
      await artistFetch(`/tours/${tourId}`, { method: 'DELETE' })
      if (expandedTourId === tourId) setExpandedTourId(null)
      qc.invalidateQueries({ queryKey: ['tours'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  return (
    <div>
      <PageHeader
        title="Tours"
        subtitle="Organise shows into tours with legs, party members, and per-leg roster overrides."
        actions={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setShowTemplates((t) => !t); if (creating) setCreating(false) }}
              className="flex items-center gap-2 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm font-medium hover:bg-ovation-ink/5"
            >
              {showTemplates ? 'Hide templates' : 'Templates'}
            </button>
            <button
              type="button"
              onClick={() => { setCreating((c) => !c); if (showTemplates) setShowTemplates(false) }}
              className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
            >
              <Plus className="h-4 w-4" />
              {creating ? 'Cancel' : 'Create tour'}
            </button>
          </div>
        }
      />

      {showTemplates && (
        <div className="mb-6">
          <TourTemplatesInline />
        </div>
      )}

      {creating && (
        <CreateTourForm
          existingTours={data ?? []}
          onCreated={() => {
            setCreating(false)
            qc.invalidateQueries({ queryKey: ['tours'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        {(data ?? []).length === 0 ? (
          <div className="rounded-lg border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No tours yet. Create one to start organising your shows.
          </div>
        ) : (
          <div className="space-y-3">
            {(data ?? []).map((tour) => (
              <TourCard
                key={tour.id}
                tour={tour}
                expanded={expandedTourId === tour.id}
                onToggle={() => toggleTour(tour.id)}
                onDelete={() => deleteTour(tour.id)}
              />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}

/* ── Tour card with inline edit ── */

function TourCard({
  tour,
  expanded,
  onToggle,
  onDelete,
}: {
  tour: Tour
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(tour.name)
  const [editDesc, setEditDesc] = useState(tour.description ?? '')
  const [editStart, setEditStart] = useState(tour.startDate?.slice(0, 10) ?? '')
  const [editEnd, setEditEnd] = useState(tour.endDate?.slice(0, 10) ?? '')
  const [editStatus, setEditStatus] = useState(tour.status)
  const [saving, setSaving] = useState(false)

  async function saveTour() {
    setSaving(true)
    try {
      await artistFetch(`/tours/${tour.id}`, {
        method: 'PATCH',
        body: {
          name: editName.trim(),
          description: editDesc.trim() || undefined,
          startDate: editStart || undefined,
          endDate: editEnd || undefined,
          status: editStatus,
        },
      })
      qc.invalidateQueries({ queryKey: ['tours'] })
      setEditing(false)
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error saving tour')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white">
      {/* Tour header */}
      <div className="flex items-center gap-3 p-4">
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 rounded p-0.5 hover:bg-ovation-ink/5"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-ovation-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-ovation-muted" />
          )}
        </button>

        {editing ? (
          <div className="flex-1 space-y-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm font-medium"
                placeholder="Tour name"
              />
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <input
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
              placeholder="Description (optional)"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={editStart}
                onChange={(e) => setEditStart(e.target.value)}
                className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
              />
              <input
                type="date"
                value={editEnd}
                onChange={(e) => setEditEnd(e.target.value)}
                className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveTour}
                disabled={saving || !editName.trim()}
                className="rounded-md bg-ovation-ink px-3 py-1 text-xs font-medium text-ovation-paper disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-md border border-ovation-ink/15 px-3 py-1 text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className="flex-1 min-w-0 cursor-pointer"
            onClick={onToggle}
          >
            <div className="flex items-center gap-2">
              <span className="font-display text-lg font-medium">
                {tour.name}
              </span>
              <span className="rounded-full bg-ovation-ink/10 px-2 py-0.5 text-xs capitalize">
                {tour.status.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-ovation-muted">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {dateRange(tour.startDate, tour.endDate)}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {tour.memberCount} member{tour.memberCount !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {tour.legCount} leg{tour.legCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {!editing && (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-ovation-ink/15 px-3 py-1 text-xs hover:bg-ovation-ink/5"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded-md border border-ovation-ink/15 px-3 py-1 text-xs text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Expanded detail */}
      {expanded && <TourDetail tourId={tour.id} />}
    </div>
  )
}

/* ── Create tour form ── */

interface TourTemplate {
  id: string
  name: string
  isDefault: boolean
  artistId: string | null
  _count?: { roles: number }
}

function CreateTourForm({ existingTours, onCreated }: { existingTours: Tour[]; onCreated: () => void }) {
  const [mode, setMode] = useState<'blank' | 'template' | 'clone'>('blank')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [cloneFromTourId, setCloneFromTourId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const templates = useQuery<TourTemplate[], ApiClientError>({
    queryKey: ['tour-templates'],
    queryFn: () => artistFetch<TourTemplate[]>('/tour-templates'),
  })

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setError(null)
    setSubmitting(true)
    try {
      // 1. Create the tour
      const tour = await artistFetch<{ id: string }>('/tours', {
        method: 'POST',
        body: {
          name: name.trim(),
          description: description || undefined,
          startDate: startDate ? new Date(startDate).toISOString() : undefined,
          endDate: endDate ? new Date(endDate).toISOString() : undefined,
        },
      })

      // 2. Apply template if selected
      if (mode === 'template' && selectedTemplateId) {
        await artistFetch(`/tour-templates/${selectedTemplateId}/apply-to-tour/${tour.id}`, {
          method: 'POST',
        }).catch(() => {}) // Don't fail the whole creation if template apply fails
      }

      // 3. Clone from previous tour if selected
      if (mode === 'clone' && cloneFromTourId) {
        // Get the source tour's members and recreate them
        const members = await artistFetch<Array<{ name: string; role: string }>>(`/tours/${cloneFromTourId}/members`).catch(() => [])
        for (const m of members) {
          await artistFetch(`/tours/${tour.id}/members`, {
            method: 'POST',
            body: { name: m.name, role: m.role },
          }).catch(() => {})
        }
      }

      onCreated()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  const completedTours = existingTours.filter((t) => t.status !== 'cancelled')
  const modeClass = (m: string) =>
    `flex-1 rounded-md px-3 py-2 text-sm font-medium text-center cursor-pointer transition-colors ${
      mode === m
        ? 'bg-ovation-ink text-ovation-paper'
        : 'border border-ovation-ink/15 hover:bg-ovation-ink/5'
    }`

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <h3 className="font-display text-lg">Create a new tour</h3>

      {/* Mode selector */}
      <div className="flex gap-2">
        <button type="button" onClick={() => setMode('blank')} className={modeClass('blank')}>
          Start blank
        </button>
        <button type="button" onClick={() => setMode('template')} className={modeClass('template')}>
          From template
        </button>
        {completedTours.length > 0 && (
          <button type="button" onClick={() => setMode('clone')} className={modeClass('clone')}>
            Clone previous tour
          </button>
        )}
      </div>

      {/* Template selector */}
      {mode === 'template' && (
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Choose a tour template
          </label>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">— select a template —</option>
            {(templates.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} {t.artistId === null ? '(System default)' : ''} {t.isDefault ? '(Default)' : ''}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ovation-muted">
            Template roles will be added as tour members automatically.
          </p>
        </div>
      )}

      {/* Clone selector */}
      {mode === 'clone' && (
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Clone from previous tour
          </label>
          <select
            value={cloneFromTourId}
            onChange={(e) => {
              setCloneFromTourId(e.target.value)
              const src = completedTours.find((t) => t.id === e.target.value)
              if (src && !name) setName(`${src.name} (copy)`)
            }}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">— select a tour to clone —</option>
            {completedTours.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {t.memberCount} members, {t.legCount} legs ({t.status})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-ovation-muted">
            Members from the selected tour will be copied to the new tour.
          </p>
        </div>
      )}

      {/* Tour details */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Name *</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="European Summer 2026" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Start date</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">End date</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Optional description" />
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting || (mode === 'template' && !selectedTemplateId) || (mode === 'clone' && !cloneFromTourId)} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Creating...' : mode === 'template' ? 'Create from template' : mode === 'clone' ? 'Clone tour' : 'Create tour'}
      </button>
    </form>
  )
}

/* ── Inline tour templates (shown when Templates button clicked) ── */

function TourTemplatesInline() {
  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl">Tour Role Templates</h2>
        <Link
          to="/tour-templates"
          className="text-sm text-ovation-muted hover:text-ovation-ink hover:underline"
        >
          Manage templates
        </Link>
      </div>
      <p className="text-sm text-ovation-muted">
        Templates define the standard roles (seats) needed for a tour. When you create a tour from a template,
        all roles are pre-populated as empty seats ready to be filled.
        Click "Manage templates" to create, edit, or clone templates.
      </p>
    </div>
  )
}

/* ── Tour detail (members + legs) ── */

function TourDetail({ tourId }: { tourId: string }) {
  const qc = useQueryClient()

  const members = useQuery<TourMember[], ApiClientError>({
    queryKey: ['tours', tourId, 'members'],
    queryFn: () => artistFetch<TourMember[]>(`/tours/${tourId}/members`),
  })

  const legs = useQuery<TourLeg[], ApiClientError>({
    queryKey: ['tours', tourId, 'legs'],
    queryFn: () => artistFetch<TourLeg[]>(`/tours/${tourId}/legs`),
  })

  return (
    <div className="border-t border-ovation-ink/10 p-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: Members */}
        <div>
          <h3 className="font-display text-xl mb-4 flex items-center gap-2">
            <Users className="h-5 w-5" />
            Members
          </h3>
          <AsyncBoundary isLoading={members.isLoading} error={members.error}>
            <MembersPanel
              tourId={tourId}
              members={members.data ?? []}
              onChanged={() =>
                qc.invalidateQueries({
                  queryKey: ['tours', tourId, 'members'],
                })
              }
            />
          </AsyncBoundary>
        </div>

        {/* Right: Legs */}
        <div>
          <h3 className="font-display text-xl mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Legs
          </h3>
          <AsyncBoundary isLoading={legs.isLoading} error={legs.error}>
            <LegsPanel
              tourId={tourId}
              legs={legs.data ?? []}
              onChanged={() =>
                qc.invalidateQueries({
                  queryKey: ['tours', tourId, 'legs'],
                })
              }
            />
          </AsyncBoundary>
        </div>
      </div>
    </div>
  )
}

/* ── Members panel ── */

function MembersPanel({
  tourId,
  members,
  onChanged,
}: {
  tourId: string
  members: TourMember[]
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [saveSubmitting, setSaveSubmitting] = useState(false)

  async function saveAsTemplate(e: FormEvent) {
    e.preventDefault()
    if (!templateName.trim()) return
    setSaveSubmitting(true)
    try {
      await artistFetch(`/tours/${tourId}/save-as-template`, {
        method: 'POST',
        body: { name: templateName.trim() },
      })
      setSavingTemplate(false)
      setTemplateName('')
      qc.invalidateQueries({ queryKey: ['tour-templates'] })
      alert('Template saved successfully')
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error saving template')
    } finally {
      setSaveSubmitting(false)
    }
  }

  async function addMember(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    try {
      await artistFetch(`/tours/${tourId}/members`, {
        method: 'POST',
        body: { name: name.trim(), role: role.trim() },
      })
      setName('')
      setRole('')
      setAdding(false)
      onChanged()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  async function removeMember(memberId: string) {
    try {
      await artistFetch(`/tours/${tourId}/members/${memberId}`, {
        method: 'DELETE',
      })
      onChanged()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-2">
      {members.length === 0 && (
        <p className="text-sm text-ovation-muted">No members yet.</p>
      )}
      {members.map((m) => (
        <div
          key={m.id}
          className="flex items-center justify-between rounded-md border border-ovation-ink/10 px-3 py-2"
        >
          <div>
            <span className="text-sm font-medium">{m.name}</span>
            {m.role && (
              <span className="ml-2 text-sm text-ovation-muted">({m.role})</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => removeMember(m.id)}
            className="text-ovation-muted hover:text-red-600"
            title="Remove member"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}

      {adding ? (
        <form onSubmit={addMember} className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              placeholder="Name"
            />
            <RolePickerWithAdd value={role} onChange={setRole} />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs hover:bg-ovation-ink/5"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-2 flex items-center gap-1 text-sm text-ovation-muted hover:text-ovation-ink"
        >
          <Plus className="h-3.5 w-3.5" />
          Add member
        </button>
      )}

      {/* Save as tour template */}
      {members.length > 0 && (
        <div className="mt-3 border-t border-ovation-ink/5 pt-3">
          {savingTemplate ? (
            <form onSubmit={saveAsTemplate} className="flex gap-2">
              <input
                required
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="flex-1 rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
                placeholder="Template name"
              />
              <button
                type="submit"
                disabled={saveSubmitting}
                className="rounded-md bg-ovation-ink px-2 py-1.5 text-xs font-medium text-ovation-paper disabled:opacity-50"
              >
                {saveSubmitting ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setSavingTemplate(false)}
                className="rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
              >
                Cancel
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setSavingTemplate(true)}
              className="flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
            >
              <Save className="h-3.5 w-3.5" />
              Save as tour template
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Legs panel ── */

interface LegTemplate {
  id: string
  name: string
  isDefault: boolean
  artistId: string | null
  _count?: { roles: number }
}

function LegsPanel({
  tourId,
  legs,
  onChanged,
}: {
  tourId: string
  legs: TourLeg[]
  onChanged: () => void
}) {
  const [adding, setAdding] = useState(false)
  const [legName, setLegName] = useState('')
  const [addMode, setAddMode] = useState<'blank' | 'template' | 'clone'>('blank')
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [cloneLegId, setCloneLegId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [expandedLegId, setExpandedLegId] = useState<string | null>(null)

  const legTemplates = useQuery<LegTemplate[], ApiClientError>({
    queryKey: ['leg-templates'],
    queryFn: () => artistFetch<LegTemplate[]>('/leg-templates'),
    enabled: adding,
  })

  // All legs across all tours for clone source
  const allTours = useQuery<Tour[], ApiClientError>({
    queryKey: ['tours'],
    queryFn: () => artistFetch<Tour[]>('/tours'),
    enabled: adding && addMode === 'clone',
  })

  async function addLeg(e: FormEvent) {
    e.preventDefault()
    if (!legName.trim()) return
    setSubmitting(true)
    try {
      // 1. Create the leg
      const leg = await artistFetch<{ id: string }>(`/tours/${tourId}/legs`, {
        method: 'POST',
        body: { name: legName.trim() },
      })

      // 2. Apply template if selected
      if (addMode === 'template' && selectedTemplateId) {
        await artistFetch(`/leg-templates/${selectedTemplateId}/apply-to-leg/${leg.id}`, {
          method: 'POST',
        }).catch(() => {})
      }

      // 3. Clone from previous leg if selected
      if (addMode === 'clone' && cloneLegId) {
        // Find the source leg's overrides (from the legs data we already have, or fetch)
        const sourceLeg = legs.find((l) => l.id === cloneLegId)
        if (sourceLeg) {
          for (const o of sourceLeg.overrides) {
            await artistFetch(`/tours/${tourId}/legs/${leg.id}/overrides`, {
              method: 'POST',
              body: { name: o.name, role: o.role, action: o.action, reason: o.reason },
            }).catch(() => {})
          }
        }
      }

      setLegName('')
      setAddMode('blank')
      setSelectedTemplateId('')
      setCloneLegId('')
      setAdding(false)
      onChanged()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  async function deleteLeg(legId: string) {
    if (!confirm('Delete this leg?')) return
    try {
      await artistFetch(`/tours/${tourId}/legs/${legId}`, {
        method: 'DELETE',
      })
      if (expandedLegId === legId) setExpandedLegId(null)
      onChanged()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  const addModeClass = (m: string) =>
    `flex-1 rounded-md px-2 py-1 text-xs font-medium text-center cursor-pointer transition-colors ${
      addMode === m
        ? 'bg-ovation-ink text-ovation-paper'
        : 'border border-ovation-ink/15 hover:bg-ovation-ink/5'
    }`

  return (
    <div className="space-y-2">
      {legs.length === 0 && (
        <p className="text-sm text-ovation-muted">No legs yet.</p>
      )}
      {legs.map((leg) => (
        <div
          key={leg.id}
          className="rounded-md border border-ovation-ink/10"
        >
          <button
            type="button"
            onClick={() =>
              setExpandedLegId((prev) => (prev === leg.id ? null : leg.id))
            }
            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-ovation-ink/[0.02]"
          >
            {expandedLegId === leg.id ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ovation-muted" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ovation-muted" />
            )}
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium">{leg.name}</span>
              {(leg.startDate || leg.endDate) && (
                <span className="ml-2 text-xs text-ovation-muted">
                  {dateRange(leg.startDate, leg.endDate)}
                </span>
              )}
              <span className="ml-2 text-xs text-ovation-muted">
                ({leg.shows.length} show{leg.shows.length !== 1 ? 's' : ''})
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                deleteLeg(leg.id)
              }}
              className="text-ovation-muted hover:text-red-600"
              title="Delete leg"
            >
              <X className="h-4 w-4" />
            </button>
          </button>

          {expandedLegId === leg.id && (
            <LegDetail tourId={tourId} leg={leg} onChanged={onChanged} />
          )}
        </div>
      ))}

      {adding ? (
        <form onSubmit={addLeg} className="mt-2 space-y-2 rounded-md border border-ovation-ink/10 p-3">
          {/* Mode selector */}
          <div className="flex gap-1">
            <button type="button" onClick={() => setAddMode('blank')} className={addModeClass('blank')}>
              Blank
            </button>
            <button type="button" onClick={() => setAddMode('template')} className={addModeClass('template')}>
              From template
            </button>
            {legs.length > 0 && (
              <button type="button" onClick={() => setAddMode('clone')} className={addModeClass('clone')}>
                Clone leg
              </button>
            )}
          </div>

          {/* Template selector */}
          {addMode === 'template' && (
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
            >
              <option value="">-- select a leg template --</option>
              {(legTemplates.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.artistId === null ? '(System)' : ''} {t.isDefault ? '(Default)' : ''}
                  {t._count?.roles ? ` [${t._count.roles} roles]` : ''}
                </option>
              ))}
            </select>
          )}

          {/* Clone source selector */}
          {addMode === 'clone' && (
            <select
              value={cloneLegId}
              onChange={(e) => {
                setCloneLegId(e.target.value)
                const src = legs.find((l) => l.id === e.target.value)
                if (src && !legName) setLegName(`${src.name} (copy)`)
              }}
              className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
            >
              <option value="">-- select a leg to clone --</option>
              {legs.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.shows.length} shows, {l.overrides.length} overrides)
                </option>
              ))}
            </select>
          )}

          <div className="flex gap-2">
            <input
              required
              value={legName}
              onChange={(e) => setLegName(e.target.value)}
              className="flex-1 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              placeholder="Leg name (e.g. UK & Ireland)"
            />
            <button
              type="submit"
              disabled={submitting || (addMode === 'template' && !selectedTemplateId) || (addMode === 'clone' && !cloneLegId)}
              className="rounded-md bg-ovation-ink px-3 py-2 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {submitting ? 'Adding...' : addMode === 'template' ? 'Create from template' : addMode === 'clone' ? 'Clone' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setAddMode('blank') }}
              className="rounded-md border border-ovation-ink/15 px-3 py-2 text-xs hover:bg-ovation-ink/5"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-2 flex items-center gap-1 text-sm text-ovation-muted hover:text-ovation-ink"
        >
          <Plus className="h-3.5 w-3.5" />
          Add leg
        </button>
      )}
    </div>
  )
}

/* ── Leg detail (shows, overrides, roster) ── */

function LegDetail({
  tourId,
  leg,
  onChanged,
}: {
  tourId: string
  leg: TourLeg
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const [showRoster, setShowRoster] = useState(false)
  const [addingOverride, setAddingOverride] = useState<'add' | 'remove' | null>(
    null,
  )
  const [savingAsTemplate, setSavingAsTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [saveSubmitting, setSaveSubmitting] = useState(false)

  async function saveAsTemplate(e: FormEvent) {
    e.preventDefault()
    if (!templateName.trim()) return
    setSaveSubmitting(true)
    try {
      await artistFetch(`/tours/${tourId}/legs/${leg.id}/save-as-template`, {
        method: 'POST',
        body: { name: templateName.trim() },
      })
      setSavingAsTemplate(false)
      setTemplateName('')
      qc.invalidateQueries({ queryKey: ['leg-templates'] })
      alert('Template saved successfully')
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error saving template')
    } finally {
      setSaveSubmitting(false)
    }
  }

  // Fetch all shows for the assign dropdown
  const allShows = useQuery<Show[], ApiClientError>({
    queryKey: ['shows'],
    queryFn: () => artistFetch<Show[]>('/shows'),
  })

  // Effective roster (fetched on demand)
  const roster = useQuery<RosterMember[], ApiClientError>({
    queryKey: ['tours', tourId, 'legs', leg.id, 'roster'],
    queryFn: () =>
      artistFetch<RosterMember[]>(`/tours/${tourId}/legs/${leg.id}/roster`),
    enabled: showRoster,
  })

  // Shows not yet assigned to this leg
  const assignedShowIds = new Set(leg.shows.map((s) => s.showId))
  const availableShows = (allShows.data ?? []).filter(
    (s) => !assignedShowIds.has(s.id),
  )

  async function assignShow(showId: string) {
    try {
      await artistFetch(`/tours/${tourId}/legs/${leg.id}/shows`, {
        method: 'POST',
        body: { showId },
      })
      onChanged()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  async function unassignShow(showId: string) {
    try {
      await artistFetch(`/tours/${tourId}/legs/${leg.id}/shows/${showId}`, {
        method: 'DELETE',
      })
      onChanged()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  async function removeOverride(overrideId: string) {
    try {
      await artistFetch(
        `/tours/${tourId}/legs/${leg.id}/overrides/${overrideId}`,
        { method: 'DELETE' },
      )
      onChanged()
      qc.invalidateQueries({
        queryKey: ['tours', tourId, 'legs', leg.id, 'roster'],
      })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    }
  }

  return (
    <div className="border-t border-ovation-ink/10 px-3 py-3 space-y-4">
      {/* Assigned shows */}
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
          Shows
        </h4>
        {leg.shows.length === 0 ? (
          <p className="text-xs text-ovation-muted">No shows assigned.</p>
        ) : (
          <div className="space-y-1">
            {leg.shows.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between rounded bg-ovation-ink/[0.03] px-2 py-1.5"
              >
                <div className="text-sm">
                  <Link
                    to={`/shows/${s.showId}`}
                    className="font-medium hover:underline"
                  >
                    {s.title}
                  </Link>
                  <span className="ml-2 text-ovation-muted">
                    {formatDate(s.date)} — {s.city}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => unassignShow(s.showId)}
                  className="text-ovation-muted hover:text-red-600"
                  title="Unassign show"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Assign show dropdown */}
        {availableShows.length > 0 && (
          <div className="mt-2">
            <select
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  assignShow(e.target.value)
                  e.target.value = ''
                }
              }}
              className="rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
            >
              <option value="">+ Assign show...</option>
              {availableShows.map((s) => (
                <option key={s.id} value={s.id}>
                  {formatDate(s.date)} — {s.title} ({s.city})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Member overrides */}
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
          Member overrides
        </h4>
        {leg.overrides.length === 0 ? (
          <p className="text-xs text-ovation-muted">
            No overrides — using base tour roster.
          </p>
        ) : (
          <div className="space-y-1">
            {leg.overrides.map((o) => (
              <div
                key={o.id}
                className="flex items-center justify-between rounded bg-ovation-ink/[0.03] px-2 py-1.5"
              >
                <div className="text-sm">
                  <span
                    className={
                      o.action === 'add'
                        ? 'text-emerald-700'
                        : 'text-red-700'
                    }
                  >
                    {o.action === 'add' ? 'Added' : 'Removed'}:
                  </span>{' '}
                  <span className="font-medium">{o.name}</span>
                  {o.role && (
                    <span className="text-ovation-muted"> ({o.role})</span>
                  )}
                  {o.reason && (
                    <span className="ml-1 text-xs text-ovation-muted">
                      — {o.reason}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeOverride(o.id)}
                  className="text-ovation-muted hover:text-red-600"
                  title="Remove override"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() =>
              setAddingOverride((prev) => (prev === 'add' ? null : 'add'))
            }
            className="flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
          >
            <UserPlus className="h-3.5 w-3.5" />
            Add to this leg
          </button>
          <button
            type="button"
            onClick={() =>
              setAddingOverride((prev) =>
                prev === 'remove' ? null : 'remove',
              )
            }
            className="flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
          >
            <UserMinus className="h-3.5 w-3.5" />
            Remove from this leg
          </button>
        </div>

        {addingOverride && (
          <AddOverrideForm
            tourId={tourId}
            legId={leg.id}
            action={addingOverride}
            onDone={() => {
              setAddingOverride(null)
              onChanged()
              qc.invalidateQueries({
                queryKey: ['tours', tourId, 'legs', leg.id, 'roster'],
              })
            }}
          />
        )}
      </div>

      {/* Save as template */}
      <div>
        {savingAsTemplate ? (
          <form onSubmit={saveAsTemplate} className="flex gap-2">
            <input
              required
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="flex-1 rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
              placeholder="Template name (e.g. International Leg)"
            />
            <button
              type="submit"
              disabled={saveSubmitting}
              className="rounded-md bg-ovation-ink px-2 py-1.5 text-xs font-medium text-ovation-paper disabled:opacity-50"
            >
              {saveSubmitting ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setSavingAsTemplate(false)}
              className="rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
            >
              Cancel
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => { setSavingAsTemplate(true); setTemplateName(leg.name) }}
            className="flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
          >
            <Save className="h-3.5 w-3.5" />
            Save as leg template
          </button>
        )}
      </div>

      {/* Effective roster */}
      <div>
        <button
          type="button"
          onClick={() => setShowRoster((prev) => !prev)}
          className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ovation-muted hover:text-ovation-ink"
        >
          {showRoster ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          Effective roster
        </button>
        {showRoster && (
          <div className="mt-2">
            {roster.isLoading ? (
              <p className="text-xs text-ovation-muted">Loading roster...</p>
            ) : roster.error ? (
              <p className="text-xs text-red-700">{roster.error.message}</p>
            ) : (roster.data ?? []).length === 0 ? (
              <p className="text-xs text-ovation-muted">
                Roster is empty. Add tour members first.
              </p>
            ) : (
              <div className="space-y-1">
                {(roster.data ?? []).map((r, i) => (
                  <div
                    key={`${r.name}-${r.role}-${i}`}
                    className="flex items-center gap-2 rounded bg-ovation-ink/[0.03] px-2 py-1.5 text-sm"
                  >
                    <span className="font-medium">{r.name}</span>
                    {r.role && (
                      <span className="text-ovation-muted">({r.role})</span>
                    )}
                    <span className="ml-auto text-xs text-ovation-muted">
                      {r.source}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/* ── Add override form ── */

function AddOverrideForm({
  tourId,
  legId,
  action,
  onDone,
}: {
  tourId: string
  legId: string
  action: 'add' | 'remove'
  onDone: () => void
}) {
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    try {
      await artistFetch(`/tours/${tourId}/legs/${legId}/overrides`, {
        method: 'POST',
        body: {
          name: name.trim(),
          role: role.trim(),
          action,
          reason: reason.trim() || undefined,
        },
      })
      onDone()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-2 space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
          placeholder="Name"
        />
        <input
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
          placeholder="Role"
        />
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
          placeholder="Reason (optional)"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {submitting
            ? 'Saving...'
            : action === 'add'
              ? 'Add override'
              : 'Remove override'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs hover:bg-ovation-ink/5"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
